import React, { useState } from "react";
import { inject, observer } from "mobx-react";
import Modal from "./Modal";
import api from "../Utils/api";
import { showToast } from "../Utils/toast";

// Picker for feeding the DJ queue. Lists the user's playlists; selecting one
// loads its songs so you can feed the whole playlist or individual tracks.
// Every feed appends (dedup handled in DjStore.appendToQueue), so feeding the
// same or another playlist repeatedly accumulates.
function FeedQueueModal({ isOpen, onClose, PlaylistStore, DjStore, RecentUsersStore }) {
  const [selected, setSelected] = useState(null); // playlist object being browsed
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);

  const queuedIds = new Set(DjStore.queue.map((t) => t.mediaId));

  const fetchSongs = (playlist) => {
    if (playlist.id === "my-songs") {
      const userId = RecentUsersStore.currentUser?.userId;
      return api.getUserSongs(userId);
    }
    if (playlist.id === "public") {
      return api.getSongList();
    }
    return api.getSongList(playlist.id);
  };

  const openPlaylist = async (playlist) => {
    setSelected(playlist);
    setLoading(true);
    setSongs([]);
    try {
      const { list } = await fetchSongs(playlist);
      setSongs(list || []);
    } catch (error) {
      showToast.error(`Failed to load "${playlist.title}"`);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  const feedAll = () => {
    const fresh = songs.filter((s) => !DjStore.queue.some((t) => t.mediaId === s.mediaId));
    DjStore.appendToQueue(songs);
    showToast.success(
      fresh.length
        ? `Added ${fresh.length} ${fresh.length === 1 ? "song" : "songs"} to the queue`
        : "All songs are already in the queue"
    );
  };

  const feedOne = (song) => {
    DjStore.appendToQueue([song]);
  };

  const handleClose = () => {
    setSelected(null);
    setSongs([]);
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={selected ? `Feed · ${selected.title}` : "Feed DJ Queue"}
      size="md"
    >
      {!selected ? (
        <div className="space-y-1 max-h-[60vh] overflow-y-auto thin-scroll">
          {PlaylistStore.userPlaylists.length === 0 && (
            <p className="text-sm text-center text-gray-400 py-8">
              No playlists available.
            </p>
          )}
          {PlaylistStore.userPlaylists.map((playlist) => (
            <button
              key={playlist.id}
              onClick={() => openPlaylist(playlist)}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors text-left"
            >
              <span className="flex-1 min-w-0 text-sm text-text truncate">
                {playlist.title}
              </span>
              <span className="text-text-muted text-lg leading-none">›</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setSelected(null);
                setSongs([]);
              }}
              className="text-sm text-text-muted hover:text-text transition-colors"
            >
              ‹ Back
            </button>
            <div className="flex-1" />
            <button
              onClick={feedAll}
              disabled={loading || songs.length === 0}
              className="px-3 py-1.5 text-xs rounded font-medium bg-accent text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Add entire playlist
            </button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-accent" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          )}

          {!loading && (
            <div className="space-y-1 max-h-[50vh] overflow-y-auto thin-scroll">
              {songs.length === 0 && (
                <p className="text-sm text-center text-gray-400 py-8">
                  This playlist is empty.
                </p>
              )}
              {songs.map((song) => {
                const queued = queuedIds.has(song.mediaId);
                return (
                  <div
                    key={song.mediaId}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors"
                  >
                    <div
                      className="w-9 h-9 rounded bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url(${song.albumArtUrl})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="m-0 text-sm text-text truncate">{song.title}</p>
                      <p className="m-0 text-xs text-text-muted truncate">{song.artist}</p>
                    </div>
                    <button
                      onClick={() => feedOne(song)}
                      disabled={queued}
                      className="px-2 py-1 text-xs rounded font-medium bg-neutral-700 text-text hover:bg-accent hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-neutral-700 disabled:hover:text-text"
                    >
                      {queued ? "Added" : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

export default inject("PlaylistStore", "DjStore", "RecentUsersStore")(observer(FeedQueueModal));
